// ═══════════════════════════════════════════════════════════════
// TicketHtmlView — Renders a TDMS-style load ticket from DB data.
// No PDF needed — all fields come from the ticket record + TMETA.
// ═══════════════════════════════════════════════════════════════

import { useRef } from 'react';
import { Button, Group } from '../gab/GabComponents';
import { IconPrinter } from '@tabler/icons-react';

interface TicketData {
  id: number;
  ticketNumber: string;
  debrisType: string;
  calculatedCubicYards: string;
  status: string;
  loadingMonitorName: string;
  towerMonitorName: string;
  loadingLoadPct: string;
  towerLoadPct: string;
  meta: Record<string, string>;
}

interface TruckInfo {
  truckNumber: string;
  licensePlate: string;
  truckType: string;
  capacityCY: string;
  driverName: string;
  certStatus: string;
}

interface SiteInfo {
  siteName: string;
  siteType: string;
  address: string;
}

interface ContractorInfo {
  companyName: string;
  contractNumber: string;
}

interface Props {
  ticket: TicketData;
  truck: TruckInfo | null;
  site: SiteInfo | null;
  contractor: ContractorInfo | null;
}

const DEBRIS_LABELS: Record<string, string> = {
  vegetative: 'Vegetative',
  cd: 'C&D',
  mixed: 'Mixed',
  hm: 'Hazardous Material',
  white_goods: 'White Goods',
  electronics: 'Electronics',
};

function fmtCY(v: string | number) {
  const n = parseFloat(String(v));
  return isNaN(n) ? '—' : n.toFixed(2) + ' CY';
}
function fmtMon(v: string | number) {
  const n = parseFloat(String(v));
  return isNaN(n) ? '—' : '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
function fmtDate(s: string) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const row = (label: string, value: string | undefined, mono = false) =>
  value ? (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 160, flexShrink: 0, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#1e293b', fontFamily: mono ? 'monospace' : undefined, fontWeight: mono ? 600 : undefined }}>{value}</span>
    </div>
  ) : null;

export default function TicketHtmlView({ ticket, truck, site, contractor }: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const m = ticket.meta;

  const cy = parseFloat(ticket.calculatedCubicYards) || parseFloat(m.transactionQuantity || '0') || 0;
  const rate = parseFloat(m.transactionRate || '0');
  const cost = parseFloat(m.extendedCost || '0') || (cy * rate);
  const loadPct = m.loadPercentage || ticket.loadingLoadPct || ticket.towerLoadPct || '';
  const truckNum = truck?.truckNumber || m.truckNumber || '—';
  const truckCap = truck?.capacityCY || m.truckCapacity || '';
  const capOk = truckCap ? cy <= parseFloat(truckCap) * 1.05 : null;

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Load Ticket ${ticket.ticketNumber}</title>
      <style>
        body{font-family:sans-serif;padding:24px;color:#1e293b}
        .section{margin-bottom:16px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
        .section-header{background:#1e293b;color:white;padding:6px 12px;font-size:12px;font-weight:700;letter-spacing:.05em;text-transform:uppercase}
        .section-body{padding:8px 12px}
        .field{display:flex;gap:8px;padding:4px 0;border-bottom:1px solid #f1f5f9;font-size:12px}
        .label{width:140px;flex-shrink:0;color:#64748b;text-transform:uppercase;font-size:10px;letter-spacing:.04em;padding-top:1px}
        .value{color:#1e293b}
        .mono{font-family:monospace;font-weight:700}
        .header{text-align:center;margin-bottom:20px;border-bottom:3px solid #1e293b;padding-bottom:12px}
        h1{margin:0;font-size:22px}
        .tn{font-size:14px;color:#4B3FFF;font-family:monospace;font-weight:700;margin-top:4px}
        .two-col{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        @media print{body{padding:8px}.section{break-inside:avoid}}
      </style></head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: 12,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
  };
  const headerStyle: React.CSSProperties = {
    background: '#1e293b',
    color: 'white',
    padding: '6px 12px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  };
  const bodyStyle: React.CSSProperties = { padding: '8px 12px' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Group gap="sm" p="sm" style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
        <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />} onClick={handlePrint}>
          Print / Save PDF
        </Button>
      </Group>

      {/* Scrollable ticket body */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: '#f8fafc' }}>
        <div ref={printRef} style={{ maxWidth: 760, margin: '0 auto', background: 'white', borderRadius: 10, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '3px solid #1e293b' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>DEBRIS LOAD TICKET</div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'monospace', color: '#4B3FFF' }}>{ticket.ticketNumber}</div>
            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'center', gap: 16, fontSize: 12, color: '#64748b' }}>
              <span>Type: <strong style={{ color: '#1e293b' }}>{DEBRIS_LABELS[ticket.debrisType] || ticket.debrisType}</strong></span>
              {m.ticketType && <span>Class: <strong style={{ color: '#1e293b' }}>{m.ticketType}</strong></span>}
              {m.invoiceNumber && <span>Invoice: <strong style={{ color: '#1e293b' }}>{m.invoiceNumber}</strong></span>}
            </div>
          </div>

          {/* Loading + Drop-off side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {/* LOADING */}
            <div style={sectionStyle}>
              <div style={headerStyle}>Loading</div>
              <div style={bodyStyle}>
                {row('Date', fmtDate(m.loadDate))}
                {row('Time', m.loadTime)}
                {m.pickupLat && m.pickupLng && row('GPS', `${parseFloat(m.pickupLat).toFixed(6)}, ${parseFloat(m.pickupLng).toFixed(6)}`)}
                {row('Load %', loadPct ? loadPct + '%' : undefined)}
                {row('Monitor', ticket.loadingMonitorName || m.loadMonitorId)}
                {row('Monitor ID', m.loadMonitorId)}
              </div>
            </div>

            {/* DROP OFF */}
            <div style={sectionStyle}>
              <div style={headerStyle}>Drop Off</div>
              <div style={bodyStyle}>
                {row('Date', fmtDate(m.disposalDate))}
                {row('Time', m.disposalTime)}
                {row('Disposal Site', site?.siteName || m.disposalSiteName)}
                {site?.address && row('Site Address', site.address)}
                {row('Load Call', m.loadPercentage ? m.loadPercentage + '%' : undefined)}
                {row('Monitor', ticket.towerMonitorName || m.disposalMonitorName)}
                {row('Monitor ID', m.disposalMonitorId)}
                {m.distanceMiles && row('Mileage', m.distanceMiles + ' mi')}
              </div>
            </div>
          </div>

          {/* Truck */}
          <div style={sectionStyle}>
            <div style={headerStyle}>Truck</div>
            <div style={{ ...bodyStyle, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                {row('Truck #', truckNum)}
                {row('Type', truck?.truckType || m.truckType)}
                {row('Capacity', truckCap ? truckCap + ' CY' : undefined)}
                {row('Driver', truck?.driverName)}
              </div>
              <div>
                {row('License Plate', truck?.licensePlate)}
                {row('Subcontractor', m.subcontractorName)}
                {row('Contractor', contractor?.companyName || m.contractorName)}
                {truckCap && cy > 0 && (
                  <div style={{ marginTop: 6, padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: capOk ? '#dcfce7' : '#fee2e2',
                    color: capOk ? '#037730' : '#D33423' }}>
                    {capOk ? `✓ Load OK: ${fmtCY(cy)} / ${truckCap} CY` : `⚠ Capacity exceeded: ${fmtCY(cy)} / ${truckCap} CY`}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Billing */}
          <div style={sectionStyle}>
            <div style={headerStyle}>CLIN Billing</div>
            <div style={{ ...bodyStyle, display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div>
                {row('CLIN Code', m.clinCode || '—')}
                {row('Description', m.clinDescription)}
                {row('Unit', 'CY')}
              </div>
              <div>
                {row('Cubic Yards', fmtCY(cy), true)}
                {row('Rate', rate ? fmtMon(rate) + ' / CY' : undefined, true)}
                {row('Extended Cost', cost ? fmtMon(cost) : undefined, true)}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: 8, fontSize: 10, color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
            Tampa EMC · Ticket ID {ticket.id} · DR-4834-FL
          </div>
        </div>
      </div>
    </div>
  );
}
